// QuickStart.js - Modified to integrate StartEmptyWorkout
import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Button, 
  Grid,
  Card,
  Divider,
  IconButton,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import {
  Dumbbell,
  Plus,
  Search,
  ChevronLeft,
  AlertCircle
} from 'lucide-react';
import ExerciseExplorer from './explorer/ExerciseExplorer';
import NewRoutineDialog from './routines/NewRoutineDialog';
import RoutineManager from './routines/RoutineManager';
import StartEmptyWorkout from './logworkout/StartEmptyWorkout'; // Importing the new component
import agtfitService from '../service/agtfitService';

// ===========================================
// 🎬 VARIANTES DE ANIMACIÓN TIPO macOS
// ===========================================
const containerVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

// Constants
const FIT_ORANGE = '#FF6B00';
const GRADIENT = 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Styled Components (keeping the same styles)
const QuickStartContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4, 3, 3),
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'transparent'
    : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
  maxWidth: '1200px',
  height: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 1, 2),
  }
}));

const TopBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(4),
  fontSize: '1.25rem',
}));

const GradientButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  background: GRADIENT,
  color: '#FFFFFF',
  fontWeight: 600,
  boxShadow: 'none',
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
  height: '60px',
}));

const OutlineButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: 'none',
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)')}`,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
  height: '60px',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
}));

const RoutineCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  overflow: 'hidden',
  height: '100%',
  cursor: 'pointer',
  boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : 'none',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : undefined,
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)')}`,
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
  }
}));

const AddRoutineButton = styled(Button)(({ theme }) => ({
  borderRadius: 24, // More rounded
  padding: theme.spacing(0.75, 2),
  background: GRADIENT,
  color: '#FFFFFF',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': {
    background: GRADIENT,
    boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
  }
}));

// Main Component
const QuickStart = ({ onStartEmptyWorkout, onCreateRoutine, onExploreRoutines, onBack }) => {
  // State for exercise explorer dialog
  const [openExerciseExplorer, setOpenExerciseExplorer] = useState(false);
  
  // State for new routine dialog
  const [openNewRoutineDialog, setOpenNewRoutineDialog] = useState(false);
  
  // State for routine manager
  const [openRoutineManager, setOpenRoutineManager] = useState(false);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  
  // State for routines from DynamoDB
  const [myRoutines, setMyRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for empty workout mode
  const [showEmptyWorkout, setShowEmptyWorkout] = useState(false);
  const [workoutRoutine, setWorkoutRoutine] = useState(null);
  
  // Add a ref for TodaysActivity
  const todaysActivityRef = useRef(null);

  // Load routines when component mounts
  useEffect(() => {
    loadRoutines();
  }, []);
  
  // Function to load routines from DynamoDB
  const loadRoutines = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await agtfitService.getRoutines();
      
      if (response.success) {
        setMyRoutines(response.data || []);
      } else {
        setError(response.error || 'Error loading routines');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Function to handle exercise selection
  const handleSelectExercise = (exercise) => {
    // Here you could do something with the selected exercise, like adding it to a routine
  };
  
  // Function to handle creating a new routine
  const handleNewRoutine = () => {
    setOpenNewRoutineDialog(true);
  };
  
  // Function to handle closing the new routine dialog
  const handleNewRoutineClose = async (success, routineData) => {
    setOpenNewRoutineDialog(false);
    
    if (success && routineData) {
      try {
        // Save the routine to DynamoDB
        const response = await agtfitService.createRoutine(routineData);
        
        if (response.success) {
          // Reload the routines
          await loadRoutines();
        } else {
          console.error('Error saving routine:', response.error);
        }
      } catch (err) {
        console.error('Error creating routine:', err);
      }
    }
  };
  
  // Function to open an existing routine
  const handleOpenRoutine = (routine) => {
    setSelectedRoutine(routine);
    setOpenRoutineManager(true);
  };
  
  // Function to handle closing the routine manager
  const handleRoutineManagerClose = async (updated) => {
    setOpenRoutineManager(false);
    setSelectedRoutine(null);
    
    if (updated) {
      // Reload routines if updated or deleted
      await loadRoutines();
    }
  };
  
  // Function to start a workout with a routine
  const handleStartWorkout = (routine) => {
    // Save the selected routine and show the StartEmptyWorkout component
    setWorkoutRoutine(routine);
    setShowEmptyWorkout(true);
  };
  
  // Function to start an empty workout
  const handleStartEmptyWorkout = () => {
    // Don't pass a routine and show the StartEmptyWorkout component
    setWorkoutRoutine(null);
    setShowEmptyWorkout(true);
  };
  
  // Function to go back from the workout
  const handleWorkoutBack = () => {
    setShowEmptyWorkout(false);
    setWorkoutRoutine(null);
  };
  
  // Function to handle workout completion
  const handleWorkoutComplete = (stats) => {
    // Reload routines after a workout
    loadRoutines();
    
    // Update TodaysActivity with burned calories
    if (todaysActivityRef.current && stats.caloriesBurned) {
      todaysActivityRef.current.updateCaloriesFromWorkout(stats.caloriesBurned);
    }
  };
  
  // If we're in empty workout mode, show that component
  if (showEmptyWorkout) {
    return (
      <StartEmptyWorkout 
        onBack={handleWorkoutBack} 
        selectedRoutine={workoutRoutine} 
        onWorkoutComplete={handleWorkoutComplete}
        todaysActivityRef={todaysActivityRef} // Add this line
      />
    );
  }
  
  return (
    <QuickStartContainer
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <TopBar
        component={motion.div}
        variants={itemVariants}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={onBack}
            sx={{ mr: 2, color: FIT_ORANGE }}
          >
            <ChevronLeft size={24} />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
              Workout
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Start a new workout or choose from your routines
            </Typography>
          </Box>
        </Box>
      </TopBar>
      
      <Box component={motion.div} variants={itemVariants}>
        <SectionTitle>
          Quick Start
        </SectionTitle>

        <GradientButton
          onClick={handleStartEmptyWorkout}
          startIcon={<Dumbbell size={24} />}
        >
          Start New Workout
        </GradientButton>
      </Box>

      <Box component={motion.div} variants={itemVariants}>
        <SectionTitle>
          Routines
        </SectionTitle>

        <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <OutlineButton 
            onClick={handleNewRoutine}
            startIcon={<Plus size={20} />}
          >
            New Routine
          </OutlineButton>
        </Grid>
        <Grid item xs={6}>
          <OutlineButton 
            onClick={() => setOpenExerciseExplorer(true)}
            startIcon={<Search size={20} />}
          >
            Explore Exercises
          </OutlineButton>
        </Grid>
      </Grid>
      </Box>

      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          My Routines
        </Typography>
        {!loading && !error && myRoutines.length > 0 && (
          <AddRoutineButton
            startIcon={<Plus size={18} />}
            onClick={handleNewRoutine}
          >
            Add routine
          </AddRoutineButton>
        )}
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ 
          p: 3, 
          textAlign: 'center', 
          bgcolor: 'background.paper',
          border: '1px dashed',
          borderColor: 'error.main',
          borderRadius: 2,
          color: 'error.main'
        }}>
          <AlertCircle size={24} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
          <Button 
            variant="outlined" 
            color="primary" 
            size="small" 
            sx={{ mt: 2 }}
            onClick={loadRoutines}
          >
            Retry
          </Button>
        </Box>
      ) : myRoutines.length === 0 ? (
        <Box sx={(theme) => ({
          p: 3,
          textAlign: 'center',
          bgcolor: theme.palette.actualMode === 'wallpaper'
            ? WALLPAPER_COLORS.bg
            : 'background.paper',
          backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
          WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
          border: theme.palette.actualMode === 'wallpaper'
            ? `1px solid ${WALLPAPER_COLORS.border}`
            : '1px dashed',
          borderColor: theme.palette.actualMode === 'wallpaper'
            ? WALLPAPER_COLORS.border
            : 'divider',
          borderRadius: 2
        })}>
          <Dumbbell size={32} color="#bdbdbd" />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You don't have any saved routines
          </Typography>
          <Button
            sx={{
              mt: 2,
              background: GRADIENT,
              color: '#FFFFFF',
              textTransform: 'none',
              borderRadius: 12,
              padding: '8px 16px',
              fontWeight: 600,
              '&:hover': {
                background: GRADIENT,
                boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
              }
            }}
            onClick={handleNewRoutine}
            startIcon={<Plus size={18} />}
          >
            Create routine
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {myRoutines.map(routine => (
            <Grid item xs={12} sm={6} md={4} key={routine.routineId}>
              <RoutineCard onClick={() => handleOpenRoutine(routine)}>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 2 
                  }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: GRADIENT,
                      mr: 2
                    }}>
                      <Dumbbell size={20} color="#FFFFFF" />
                    </Box>
                    <Typography variant="h6" fontWeight={600}>
                      {routine.name}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {routine.exercises?.length || 0} exercises
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {routine.updatedAt ? new Date(routine.updatedAt).toLocaleDateString() : ''}
                    </Typography>
                  </Box>
                </Box>
              </RoutineCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog to explore exercises */}
      <ExerciseExplorer
        open={openExerciseExplorer}
        onClose={() => setOpenExerciseExplorer(false)}
        onSelectExercise={handleSelectExercise}
        fromQuickStart={true}  // Add this prop
      />
      
      {/* Dialog to create new routine */}
      <NewRoutineDialog
        open={openNewRoutineDialog}
        onClose={handleNewRoutineClose}
      />
      
      {/* Routine manager */}
      <RoutineManager
        open={openRoutineManager}
        onClose={handleRoutineManagerClose}
        selectedRoutine={selectedRoutine}
        onStartWorkout={handleStartWorkout}
      />
    </QuickStartContainer>
  );
};

export default QuickStart;